/**
 * Light and dark on the page. With no saved choice the page follows prefers-color-scheme (tokens.css).
 * A choice sets data-theme and is remembered in this browser.
 */
import { useEffect, useState } from 'react';

const KEY = 'grc.theme';
export type ThemeChoice = 'light' | 'dark';

export function storedTheme(): ThemeChoice | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

export function applyTheme(choice: ThemeChoice | null) {
  if (choice) document.documentElement.setAttribute('data-theme', choice);
  else document.documentElement.removeAttribute('data-theme');
}

export function effectiveTheme(): ThemeChoice {
  const set = document.documentElement.getAttribute('data-theme');
  if (set === 'light' || set === 'dark') return set;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="12" cy="12" r="3.25" fill="none" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M12 3v2.2M12 18.8V21M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M3 12h2.2M18.8 12H21M4.9 19.1l1.6-1.6M17.5 6.5l1.6-1.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M20 14.6A7.6 7.6 0 0 1 9.4 4 6.4 6.4 0 1 0 20 14.6z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Switches the colour scheme and remembers the choice for this browser. */
export function ThemeToggle() {
  const [effective, setEffective] = useState<ThemeChoice>(() => {
    if (typeof document === 'undefined') return 'light';
    return storedTheme() ?? effectiveTheme();
  });
  useEffect(() => {
    const stored = storedTheme();
    if (stored) {
      applyTheme(stored);
      return;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setEffective(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const next: ThemeChoice = effective === 'dark' ? 'light' : 'dark';
  const toLight = next === 'light';
  return (
    <button
      type="button"
      className="header-icon"
      aria-label={toLight ? 'Switch to light' : 'Switch to dark'}
      onClick={() => {
        applyTheme(next);
        try {
          localStorage.setItem(KEY, next);
        } catch {
          /* the choice still applies for this view */
        }
        setEffective(next);
      }}
    >
      {toLight ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}
